import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Check, Star, Users, Hotel, Utensils, Camera, Mountain, Sunrise } from "lucide-react";
import SEO, { pageSEO, schemas } from "../components/SEO";

export default function JourneyPage() {
  const highlights = [
    { icon: Sunrise, text: "Taj Mahal at sunrise and sunset — witness India's most iconic monument in its most magical light" },
    { icon: Hotel, text: "5-star palace hotel experience in Jaipur — live like royalty in a heritage property" },
    { icon: Star, text: "Ganga Aarti ceremony in Rishikesh — witness the sacred evening ritual on the Ganges" },
    { icon: Utensils, text: "Signature dinners at Bukhara (Delhi), Suvarna Mahal (Jaipur), and Ana Rasa (Himalayas)" },
    { icon: Camera, text: "Amber Fort with traditional elephant ride logistics arranged" },
    { icon: Utensils, text: "Optional hands-on cooking lesson with a local Jaipur chef" },
    { icon: Mountain, text: "Yoga and wellness session in the Himalayan foothills" },
    { icon: Users, text: "Private expert guides and luxury ground transportation throughout" },
  ];

  return (
    <div className="pt-24 md:pt-32" data-testid="journey-page">
      <SEO {...pageSEO.journey} />
      {/* Hero */}
      <section className="relative py-20 md:py-32">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/12931430/pexels-photo-12931430.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Hawa Mahal - Palace of Winds, Jaipur"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#243b5a]/90 to-[#243b5a]/60" />
        </div>
        <div className="relative z-10 container-max px-6 md:px-12 lg:px-24 text-white">
          <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">The Experience</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 mb-6">
            Awaken India Tours: <br />
            <span className="text-[#D4AF37]">Taj, Palaces & Himalayas</span>
          </h1>
          <p className="text-xl text-white/80 max-w-2xl">
            A 10-day small group luxury journey for first-time India travelers
          </p>
        </div>
      </section>

      {/* Journey Description */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg">
              <p className="text-[#4A4A4A] leading-relaxed text-lg mb-6">
                This journey is crafted for curious, comfort-seeking travelers in their mid-30s to 60s — particularly those from the U.S. East Coast experiencing India for the first time. You want the magic of the Taj Mahal, the grandeur of Jaipur's palaces, and the serenity of the Himalayan foothills — but without the chaos, uncertainty, or compromise on comfort.
              </p>
              
              <p className="text-[#4A4A4A] leading-relaxed text-lg mb-6">
                Your 10-day journey flows effortlessly from Delhi to Agra, Jaipur, and the Himalayan retreat town of Rishikesh. At every step, you'll stay in carefully selected 5-star and heritage palace hotels where hospitality, cuisine, and ambiance reflect India's finest traditions.
              </p>
              
              <p className="text-[#4A4A4A] leading-relaxed text-lg mb-6">
                Dine at legendary restaurants: Bukhara for North Indian frontier cuisine, Suvarna Mahal in a gilded palace hall, and Ana Rasa overlooking the Ganges. Witness the Taj at both sunrise and sunset. Explore Amber Fort and the City Palace with expert local guides. Wind down with yoga, river walks, and ashram visits in the Himalayas.
              </p>
              
              <p className="text-[#4A4A4A] leading-relaxed text-lg">
                This is not a mass-market tour. Hosted by Aaron, an India-born professional who has lived in the United States for over 35 years, you'll experience India through the lens of someone who understands both cultures intimately. Aaron handles the details — so you can simply be present, curious, and amazed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trip Highlights */}
      <section className="section-padding bg-[#F9F9F7]" data-testid="trip-highlights">
        <div className="container-max">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase font-medium">What Awaits You</span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#243b5a] mt-4">
              Trip Highlights
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {highlights.map((item, i) => (
              <div 
                key={i} 
                className="flex items-start gap-4 bg-white p-6 rounded-xl shadow-soft"
                data-testid={`highlight-${i}`}
              >
                <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-[#D4AF37]" />
                </div>
                <p className="text-[#4A4A4A] leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Image Gallery */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2 row-span-2">
              <img 
                src="https://images.pexels.com/photos/30638768/pexels-photo-30638768.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Taj Mahal"
                className="w-full h-full object-cover rounded-2xl"
              />
            </div>
            <div className="aspect-square">
              <img 
                src="https://images.pexels.com/photos/33689316/pexels-photo-33689316.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Palace"
                className="w-full h-full object-cover rounded-2xl"
              />
            </div>
            <div className="aspect-square">
              <img 
                src="https://images.pexels.com/photos/12174800/pexels-photo-12174800.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Himalayas"
                className="w-full h-full object-cover rounded-2xl"
              />
            </div>
            <div className="aspect-square">
              <img 
                src="https://images.pexels.com/photos/7919685/pexels-photo-7919685.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Ganga Aarti"
                className="w-full h-full object-cover rounded-2xl"
              />
            </div>
            <div className="aspect-square">
              <img 
                src="https://images.unsplash.com/photo-1759262987751-422c91923354?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxIaW1hbGF5YXMlMjBzbm93JTIwbW91bnRhaW5zJTIwbHV4dXJ5JTIwdmlld3xlbnwwfHx8fDE3NzE3MDA0OTV8MA&ixlib=rb-4.1.0&q=85"
                alt="Mountain View"
                className="w-full h-full object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#243b5a]">
        <div className="container-max text-center text-white">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
            Ready to Explore <span className="text-[#D4AF37]">Day by Day</span>?
          </h2>
          <p className="text-white/80 text-lg max-w-2xl mx-auto mb-8">
            See the complete itinerary with detailed daily activities and experiences.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/itinerary">
              <Button className="bg-[#D4AF37] hover:bg-[#B8960C] text-[#243b5a] font-semibold px-8 py-3 rounded-md transition-all" data-testid="view-full-itinerary-btn">
                View Full Itinerary
              </Button>
            </Link>
            <Link to="/contact">
              <Button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#243b5a] font-semibold px-8 py-3 rounded-md transition-all">
                Inquire Now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
